import {
  MAX_SHORT_CODE_LENGTH,
  SHORT_CODE_RELATIONS,
  type CodeSchemeDraft,
  type CounterSegment,
  type DateSegment,
  type PartSegment,
  type ReferencePart,
  type ReferenceSegment,
  type SchemeIssue,
  type SchemeNode,
  type Segment,
  type ShortCodeParts,
  type ShortCodeRelations,
} from "@erp/shared-types";
import { compileSafeRegex } from "./safeRegex";

/** What validation needs of a scheme: the draft itself and the org's stored ones. */
export type CodeSchemeShape = Pick<
  CodeSchemeDraft,
  "entityType" | "key" | "segments"
>;

const ANCHORS = new Set(["^", "$", "\\b", "\\B"]);
const UNBALANCED = "Pattern has an unbalanced parenthesis";

/** `(?<name>`, as opposed to the lookbehinds `(?<=` and `(?<!`. */
function opensNamedGroup(tokens: readonly string[], index: number): boolean {
  const [question, angle, after] = tokens.slice(index + 1, index + 4);
  return question === "?" && angle === "<" && after !== "=" && after !== "!";
}

function depthChange(token: string): number {
  if (token === "(") return 1;
  return token === ")" ? -1 : 0;
}

/** Escapes and character classes are single tokens, so `\$` and `[$]` are not anchors. */
function structureProblem(tokens: readonly string[]): string | null {
  let depth = 0;
  for (const [index, token] of tokens.entries()) {
    if (ANCHORS.has(token)) return "Patterns cannot contain anchors";
    if (opensNamedGroup(tokens, index)) {
      return "Patterns cannot contain named groups";
    }
    if (token === "\\k") {
      return "Patterns cannot refer back to another segment";
    }
    depth += depthChange(token);
    if (depth < 0) return UNBALANCED;
  }
  return depth === 0 ? null : UNBALANCED;
}

/**
 * A Part pattern as it is spliced into a scheme's regex. Beside named groups,
 * some escapes such as `[\k]` stop compiling, so a pattern is checked spliced.
 */
function partFragment(pattern: string, group: string): string {
  return `(?<${group}>(?:${pattern}))`;
}

function safetyProblem(pattern: string): string | null {
  try {
    compileSafeRegex(partFragment(pattern, "s0"), {
      disallowQuantifiedGroups: true,
    });
    return null;
  } catch (error) {
    return error instanceof Error ? error.message : "Pattern is not allowed";
  }
}

/**
 * Most ways a scheme may split a value among its segments. Reading a value
 * that does not fit tries each of them, so this bounds the regex's work.
 */
const MAX_READINGS = 100_000;
const UNBOUNDED = "Repetition must be bounded, as in {0,8} instead of * or +";

function tokenize(pattern: string): string[] {
  return pattern.match(/\\.|\[(?:\\.|[^\]\\])*\]|[\s\S]/g) ?? [];
}

/** Like `tokenize`, but a group opener and a quantifier with its lazy `?` are one token each. */
function quantifiedTokens(pattern: string): string[] {
  return (
    pattern.match(
      /\\.|\[(?:\\.|[^\]\\])*\]|\(\?:?|(?:[*+?]|\{\d+(?:,\d*)?\})\??|[\s\S]/g,
    ) ?? []
  );
}

/** How many lengths a quantifier token allows, capped by the longest code; 1 when not one. */
function repeatChoices(token: string): number {
  const greedy =
    token.length > 1 && token.endsWith("?") ? token.slice(0, -1) : token;
  if (greedy === "*" || greedy === "+") return Infinity;
  if (greedy === "?") return 2;
  const range = /^\{(\d+)(,(\d*))?\}$/.exec(greedy);
  if (!range || range[2] === undefined) return 1;
  if (range[3] === "") return Infinity;
  const min = Math.min(Number(range[1]), MAX_SHORT_CODE_LENGTH);
  return Math.min(Number(range[3]), MAX_SHORT_CODE_LENGTH) - min + 1;
}

/**
 * How many ways a pattern can match a stretch of text: the product of its
 * quantifiers' choices, summed over the branches of each alternation. Groups
 * are never quantified, so this is exact enough to bound backtracking.
 */
function readings(pattern: string): number {
  const frames = [{ done: 0, branch: 1 }];
  for (const token of quantifiedTokens(pattern)) {
    const frame = frames[frames.length - 1];
    if (token.startsWith("(")) {
      frames.push({ done: 0, branch: 1 });
    } else if (token === "|") {
      frame.done += frame.branch;
      frame.branch = 1;
    } else if (token === ")" && frames.length > 1) {
      frames.pop();
      frames[frames.length - 1].branch *= frame.done + frame.branch;
    } else {
      frame.branch *= repeatChoices(token);
    }
  }
  return frames[0].done + frames[0].branch;
}

/**
 * Why a Part pattern can't be spliced into a scheme's regex, or null when it
 * can. Anchors, named groups and unbalanced parentheses would change the
 * meaning of the segments around it, and unbounded repetition could make
 * reading a value backtrack without end.
 */
export function partPatternProblem(pattern: string): string | null {
  const problem = structureProblem(tokenize(pattern)) ?? safetyProblem(pattern);
  if (problem) return problem;
  return readings(pattern) === Infinity ? UNBOUNDED : null;
}

/**
 * How many ways the segments could split one value among themselves. A
 * Reference counts as every length a code may have, since codes are issued
 * after the scheme is saved, so a saved scheme always compiles.
 */
function schemeReadings(segments: readonly Segment[]): number {
  return segments.reduce((total, segment) => {
    if (segment.kind === "part") return total * readings(segment.pattern);
    if (segment.kind === "reference") return total * MAX_SHORT_CODE_LENGTH;
    return total;
  }, 1);
}

function ambiguityIssues(segments: readonly Segment[]): SchemeIssue[] {
  return schemeReadings(segments) > MAX_READINGS
    ? [{ kind: "too_ambiguous" }]
    : [];
}

function nodeId(node: SchemeNode): string {
  return `${node.entityType}:${node.key}`;
}

/** Schemes a scheme's Reference segments point at, via each relation's target type. */
function referencedNodes(
  scheme: CodeSchemeShape,
  relations: ShortCodeRelations,
): SchemeNode[] {
  return scheme.segments.flatMap((segment) => {
    if (segment.kind !== "reference") return [];
    const target = relations[scheme.entityType][segment.relation];
    return target ? [{ entityType: target, key: segment.scheme }] : [];
  });
}

/** A reference cycle through the draft, as the chain of schemes, or null. */
function findCycleThrough(
  draft: CodeSchemeShape,
  schemes: ReadonlyMap<string, CodeSchemeShape>,
  relations: ShortCodeRelations,
): SchemeNode[] | null {
  const done = new Set<string>();
  const path: SchemeNode[] = [];
  const visit = (node: SchemeNode): SchemeNode[] | null => {
    const id = nodeId(node);
    const onPath = path.findIndex((step) => nodeId(step) === id);
    if (onPath >= 0) return [...path.slice(onPath), node];
    if (done.has(id)) return null;
    const scheme = schemes.get(id);
    if (!scheme) return null;
    path.push(node);
    for (const next of referencedNodes(scheme, relations)) {
      const cycle = visit(next);
      if (cycle) return cycle;
    }
    path.pop();
    done.add(id);
    return null;
  };
  return visit({ entityType: draft.entityType, key: draft.key });
}

/** The name a segment's text is stored under in a code's parts, if it has one. */
function partName(segment: Segment): string | null {
  return segment.kind === "literal" || segment.kind === "counter"
    ? null
    : segment.name;
}

function duplicateNameIssues(segments: readonly Segment[]): SchemeIssue[] {
  const names = segments.flatMap((segment) => partName(segment) ?? []);
  const repeated = names.filter((name, index) => names.indexOf(name) < index);
  return [...new Set(repeated)].map((name) => ({
    kind: "duplicate_segment_name",
    name,
  }));
}

function counterIssues(segments: readonly Segment[]): SchemeIssue[] {
  const counters = segments.filter((segment) => segment.kind === "counter");
  if (counters.length > 1) return [{ kind: "multiple_counters" }];
  const names = new Set(segments.flatMap((segment) => partName(segment) ?? []));
  return (counters[0]?.scope ?? [])
    .filter((name) => !names.has(name))
    .map((name) => ({ kind: "unknown_counter_scope", name }));
}

function partIssues(segment: PartSegment): SchemeIssue[] {
  const reason = partPatternProblem(segment.pattern);
  return reason
    ? [{ kind: "unsafe_pattern", segment: segment.name, reason }]
    : [];
}

function referenceIssues(
  segment: ReferenceSegment,
  draft: CodeSchemeShape,
  schemes: ReadonlyMap<string, CodeSchemeShape>,
  relations: ShortCodeRelations,
): SchemeIssue[] {
  const target = relations[draft.entityType][segment.relation];
  if (!target) {
    return [
      {
        kind: "unknown_relation",
        segment: segment.name,
        relation: segment.relation,
      },
    ];
  }
  if (schemes.has(nodeId({ entityType: target, key: segment.scheme }))) {
    return [];
  }
  return [
    {
      kind: "referenced_scheme_missing",
      segment: segment.name,
      entityType: target,
      scheme: segment.scheme,
    },
  ];
}

/**
 * Everything that stops a draft being saved, given the organization's stored
 * schemes (the draft replaces a stored scheme with its key). Empty when the
 * draft can be saved. `relations` is injectable so the cycle rule can be
 * exercised before any real relation can close one.
 */
export function validateScheme(
  draft: CodeSchemeShape,
  orgSchemes: readonly CodeSchemeShape[],
  relations: ShortCodeRelations = SHORT_CODE_RELATIONS,
): SchemeIssue[] {
  const schemes = new Map(
    [...orgSchemes, draft].map((scheme) => [nodeId(scheme), scheme]),
  );
  const segmentIssues = draft.segments.flatMap((segment) => {
    if (segment.kind === "part") return partIssues(segment);
    if (segment.kind === "reference") {
      return referenceIssues(segment, draft, schemes, relations);
    }
    return [];
  });
  const cycle = findCycleThrough(draft, schemes, relations);
  return [
    ...duplicateNameIssues(draft.segments),
    ...counterIssues(draft.segments),
    ...segmentIssues,
    ...(segmentIssues.length === 0 ? ambiguityIssues(draft.segments) : []),
    ...(cycle ? [{ kind: "reference_cycle" as const, cycle }] : []),
  ];
}

/**
 * The codes issued under the scheme each Reference segment names, keyed by
 * segment name, active and retired alike. The caller loads them; compiling
 * never does I/O.
 */
export type IssuedReferences = Readonly<
  Record<string, readonly ReferencePart[]>
>;

export type ParseResult =
  | {
      conforms: true;
      parts: ShortCodeParts;
      counter: number | null;
      /** The values of the counter's scope; codes sharing it share a count. */
      counterScope: string;
    }
  | { conforms: false };

/** A calendar date, as the organization's time zone reads it. */
export interface CalendarDate {
  year: number;
  /** 1 to 12. */
  month: number;
  day: number;
}

export interface GenerateInputs {
  /** The number for the scheme's Counter. Required when it has one. */
  counter?: number;
  /** The issue date, for Date segments. Required when the scheme has one. */
  date?: CalendarDate;
  /** Text for each Part segment, by name. */
  parts?: Readonly<Record<string, string>>;
  /** The issued code each Reference segment takes, by name. */
  references?: Readonly<Record<string, string>>;
}

export interface CompiledScheme {
  /** Exact, case-sensitive, whole-value match. */
  parse(value: string): ParseResult;
  /**
   * Builds a value from the segments, then reads it back. Null unless every
   * segment reads as the text written for it, so a generated code conforms
   * and parses to exactly its inputs.
   */
  generate(inputs: GenerateInputs): string | null;
  /**
   * The counter scope a code generated from these inputs would have, so the
   * next number can be found before the counter is chosen.
   */
  counterScopeOf(inputs: Omit<GenerateInputs, "counter">): string;
}

export class CodeSchemeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "CodeSchemeError";
  }
}

function escapeRegex(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\/-]/g, "\\$&");
}

/** What compiling, reading and writing do for each kind of segment. */
interface SegmentKind<S extends Segment> {
  /** The regex fragment, its capture group named by position. */
  fragment(segment: S, group: string, references: IssuedReferences): string;
  /** Records the matched text into the parse result. */
  read(segment: S, text: string, result: ReadState): void;
  /** The text a generated value carries for the segment. */
  write(segment: S, inputs: GenerateInputs): string;
}

interface ReadState {
  parts: ShortCodeParts;
  counter: number | null;
  entityByCode: ReadonlyMap<string, ReadonlyMap<string, string>>;
}

/** The highest number a counter `width` digits wide can hold: 9999 for 4. */
export function maxCounter(width: number): number {
  return 10 ** width - 1;
}

/** Zero-padded to the width; a number too wide to fit is written as is and fails to read back. */
function counterText(width: number, counter: number | undefined): string {
  const valid =
    counter !== undefined && Number.isSafeInteger(counter) && counter >= 0;
  if (!valid) throw new CodeSchemeError("A non-negative counter is required");
  return String(counter).padStart(width, "0");
}

const DATE_PATTERNS: Record<DateSegment["format"], string> = {
  YYYY: "\\d{4}",
  YY: "\\d{2}",
  MM: "0[1-9]|1[0-2]",
  DD: "0[1-9]|[12]\\d|3[01]",
};

function dateText(
  format: DateSegment["format"],
  date: CalendarDate | undefined,
) {
  if (!date) throw new CodeSchemeError("A date is required");
  const pad = (n: number) => String(n).padStart(2, "0");
  if (format === "YYYY") return String(date.year).padStart(4, "0");
  if (format === "YY") return pad(date.year % 100);
  return pad(format === "MM" ? date.month : date.day);
}

function alternationOf(issued: readonly ReferencePart[] | undefined): string {
  const codes = (issued ?? [])
    .map((reference) => reference.code)
    .sort((a, b) => b.length - a.length)
    .map(escapeRegex);
  // An empty alternation must match nothing, not the empty string.
  return codes.length > 0 ? codes.join("|") : "(?!)";
}

const SEGMENT_KINDS: {
  [K in Segment["kind"]]: SegmentKind<Extract<Segment, { kind: K }>>;
} = {
  literal: {
    fragment: (segment, group) => `(?<${group}>${escapeRegex(segment.text)})`,
    read: () => undefined,
    write: (segment) => segment.text,
  },
  counter: {
    fragment: (segment, group) => `(?<${group}>\\d{${segment.width}})`,
    read: (_segment, text, result) => {
      result.counter = Number(text);
    },
    write: (segment, inputs) => counterText(segment.width, inputs.counter),
  },
  date: {
    fragment: (segment, group) =>
      `(?<${group}>${DATE_PATTERNS[segment.format]})`,
    read: (segment, text, result) => {
      result.parts[segment.name] = text;
    },
    write: (segment, inputs) => dateText(segment.format, inputs.date),
  },
  part: {
    fragment: (segment, group) => {
      const problem = partPatternProblem(segment.pattern);
      if (problem) throw new CodeSchemeError(`${segment.name}: ${problem}`);
      return partFragment(segment.pattern, group);
    },
    read: (segment, text, result) => {
      result.parts[segment.name] = text;
    },
    write: (segment, inputs) => inputs.parts?.[segment.name] ?? "",
  },
  reference: {
    fragment: (segment, group, references) =>
      `(?<${group}>${alternationOf(references[segment.name])})`,
    read: (segment, text, result) => {
      const entityId = result.entityByCode.get(segment.name)?.get(text);
      if (entityId) result.parts[segment.name] = { code: text, entityId };
    },
    write: (segment, inputs) => inputs.references?.[segment.name] ?? "",
  },
};

/** The kind's handlers, typed for the segment at hand. */
function kindOf<S extends Segment>(segment: S): SegmentKind<S> {
  const kinds: Record<Segment["kind"], SegmentKind<Segment>> = SEGMENT_KINDS;
  return kinds[segment.kind];
}

/**
 * Compiles a scheme's segments into one anchored regex that both reads codes
 * and checks generated ones. A Reference matches only the issued codes it is
 * given, longest first, so `W+` is tried before `W`. Throws `CodeSchemeError`
 * for a Part pattern `validateScheme` would reject.
 */
export function compileCodeScheme(
  segments: readonly Segment[],
  references: IssuedReferences = {},
): CompiledScheme {
  const groups = segments.map((_, index) => `s${index}`);
  const regex = new RegExp(
    `^${segments
      .map((segment, index) =>
        kindOf(segment).fragment(segment, groups[index], references),
      )
      .join("")}$`,
  );
  if (schemeReadings(segments) > MAX_READINGS) {
    throw new CodeSchemeError("The scheme is too ambiguous to read quickly");
  }
  const entityByCode = new Map(
    Object.entries(references).map(([name, issued]) => [
      name,
      new Map(issued.map((reference) => [reference.code, reference.entityId])),
    ]),
  );

  /** The text each segment matched, in segment order, or null. */
  function split(value: string): string[] | null {
    if (value.length > MAX_SHORT_CODE_LENGTH) return null;
    const matched = regex.exec(value)?.groups;
    return matched ? groups.map((group) => matched[group] ?? "") : null;
  }

  const counter = segments.find(
    (segment): segment is CounterSegment => segment.kind === "counter",
  );
  const scopeIndexes = (counter?.scope ?? []).map((name) =>
    segments.findIndex((segment) => partName(segment) === name),
  );

  /** Each scope segment's text, in scope order; empty when the count never restarts. */
  function scopeOf(texts: readonly string[]): string {
    return scopeIndexes.length > 0
      ? JSON.stringify(scopeIndexes.map((index) => texts[index]))
      : "";
  }

  function parse(value: string): ParseResult {
    const texts = split(value);
    if (!texts) return { conforms: false };
    const result: ReadState = { parts: {}, counter: null, entityByCode };
    segments.forEach((segment, index) => {
      kindOf(segment).read(segment, texts[index], result);
    });
    return {
      conforms: true,
      parts: result.parts,
      counter: result.counter,
      counterScope: scopeOf(texts),
    };
  }

  function generate(inputs: GenerateInputs): string | null {
    const written = segments.map((segment) =>
      kindOf(segment).write(segment, inputs),
    );
    const value = written.join("");
    if (value.length === 0) return null;
    const read = split(value);
    return read?.every((text, index) => text === written[index]) ? value : null;
  }

  function counterScopeOf(inputs: Omit<GenerateInputs, "counter">): string {
    return scopeOf(
      segments.map((segment, index) =>
        scopeIndexes.includes(index)
          ? kindOf(segment).write(segment, inputs)
          : "",
      ),
    );
  }

  return { parse, generate, counterScopeOf };
}
